import React, { useCallback, useEffect, useState } from "react";
import { Grid, Typography, makeStyles } from "@material-ui/core";
import { useCommonStyles } from "./styles";
import CHAT_CANDIDATE_IMG from "./images/chatCandidate.svg";
import CHAT_CANDIDATE_DARK_IMG from "./images/chatCandidate-dark.svg";
import CHAT_BOT_IMG from "./images/chatBot.svg";
import CHAT_BOT_DARK_IMG from "./images/chatBot-dark.svg";

import clsx from "clsx";
import { DateTime } from "luxon";
import { ResendChatInvite } from "./resendChatInvite";
import { getChatScript } from "controllers/matchingDetails";

const useStyles = makeStyles((theme) => ({
  chatContainer: {
    height: "calc(320px - 42px)",
    overflow: "auto",
  },
  chatWrapper: {
    display: "flex",
    maxWidth: "80%",
    textAlign: "justify",
    margin: "16px 0",
  },
  candidate: {
    flexDirection: "row-reverse",
    maxWidth: "100%",
  },
  participantIconWrapper: {
    marginRight: 16,
  },
  candidateMessageWrapper: {
    borderRadius: "12px 2px 12px 12px !important",
    marginRight: 16,
  },
  messageWrapper: {
    padding: 12,
    borderRadius: "2px 12px 12px 12px",
    background: (props) => (props.mode === "dark" ? "#282A4A" : "#F6F6F6"),
  },
  chatTimestamp: {
    fontSize: 10,
    paddingTop: 4,
  },
  title: {
    paddingBottom: 20,
  },
  titleExtraContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingBottom: "20px",
  },
  scoreText: {
    marginLeft: 12,
  },
  failedText: {
    padding: "4px 8px",
    borderRadius: "2px",
    background: "rgba(255, 102, 0, 0.15)",
    color: "#FF6600",
    lineHeight: "12px",
  },
}));

const CardTitle = ({ mode, title = "", score = "", failed = false }) => {
  const classes = useStyles({ mode });
  const commonClasses = useCommonStyles({ mode });

  return (
    <Grid container>
      <Grid
        item
        xs={6}
        classes={{
          root: clsx(commonClasses.fontMediumBold, classes.title),
        }}
      >
        {title}
      </Grid>
      {(score || failed) && (
        <Grid item xs={6} classes={{ root: classes.titleExtraContainer }}>
          {failed && (
            <Typography
              className={clsx(commonClasses.fontSmall, classes.failedText)}
            >
              FAILED
            </Typography>
          )}

          {score && (
            <Typography
              className={clsx(commonClasses.fontSmall, classes.scoreText)}
            >
              Score: {score}
            </Typography>
          )}
        </Grid>
      )}
    </Grid>
  );
};

const MatchingDetailsChatScript = ({
  mode,
  applicationId,
  chatTranscript = "{}",
}) => {
  const classes = useStyles({ mode });
  const commonClasses = useCommonStyles({ mode });
  const chatTranscriptJson = JSON.parse(chatTranscript) || {};
  const {
    candidate_score,
    disqualified = false,
    max_score,
    transcript = {},
  } = chatTranscriptJson;

  const [chatData, setChatData] = useState(null);

  const fetchChatData = useCallback(async () => {
    if (applicationId) {
      const response = await getChatScript(applicationId);

      setChatData(response?.response?.notes?.[0]?.body);
    }
  }, [applicationId]);

  useEffect(() => {
    if (!transcript?.messages?.length) {
      fetchChatData();
    }
  }, [fetchChatData, transcript?.messages]);

  if (!transcript?.messages?.length && !chatData) {
    return (
      <>
        <CardTitle title="Chat Script" mode={mode} />
        <ResendChatInvite mode={mode} />
      </>
    );
  }

  return (
    <>
      <CardTitle
        title="Chat Script"
        mode={mode}
        score={
          candidate_score && max_score ? `${candidate_score}/${max_score}` : ""
        }
        failed={disqualified}
      />
      <div className={classes.chatContainer}>
        {(transcript?.messages || JSON.parse(chatData || "{}")?.messages)?.map(
          ({ participant, text, timestamp }) => (
            <div
              className={clsx(
                classes.chatWrapper,
                participant === 1 ? classes.candidate : classes.bot
              )}
            >
              <div className={classes.participantIconWrapper}>
                <img
                  src={
                    participant === 1
                      ? mode === "dark"
                        ? CHAT_CANDIDATE_DARK_IMG
                        : CHAT_CANDIDATE_IMG
                      : mode === "dark"
                      ? CHAT_BOT_DARK_IMG
                      : CHAT_BOT_IMG
                  }
                  alt={participant === 1 ? "Candidate" : "Bot"}
                />
              </div>
              <div
                className={clsx(
                  classes.messageWrapper,
                  commonClasses.fontRegularLight,
                  participant === 1
                    ? classes.candidateMessageWrapper
                    : classes.botMessageWrapper
                )}
              >
                {text}
                <Typography
                  className={clsx(
                    commonClasses.fontRegularLight,
                    classes.chatTimestamp
                  )}
                >
                  {DateTime.fromMillis(timestamp).toFormat(
                    "dd LLL, yyyy | hh:mm a"
                  )}
                </Typography>
              </div>
            </div>
          )
        )}
      </div>
    </>
  );
};

export default MatchingDetailsChatScript;
